import { Action, createAsyncAction, createReducer, createAction } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import {
  MyVehiclesResponse,
  VehicleObject,
  VehicleAlertsResponse,
  VehicleAlertsListResponse,
  VehicleMileage,
} from '../../../../types/myVehiclesType';

// Actions
export enum Actions {
  GET_MY_VEHICLES = 'naf/myVehicles/GET_MY_VEHICLES',
  GET_MY_VEHICLES_SUCCESS = 'naf/myVehicles/GET_MY_VEHICLES_SUCCESS',
  GET_MY_VEHICLES_FAIL = 'naf/myVehicles/GET_MY_VEHICLES_FAIL',
  GET_MY_VEHICLES_CANCEL = 'naf/myVehicles/GET_MY_VEHICLES_CANCEL',
  GET_VEHICLE = 'naf/myVehicles/GET_VEHICLE',
  GET_VEHICLE_SUCCESS = 'naf/myVehicles/GET_VEHICLE_SUCCESS',
  GET_VEHICLE_FAIL = 'naf/myVehicles/GET_VEHICLE_FAIL',
  GET_VEHICLE_CANCEL = 'naf/myVehicles/GET_VEHICLE_CANCEL',
  GET_VEHICLE_ALERTS = 'naf/myVehicles/GET_VEHICLE_ALERTS',
  GET_VEHICLE_ALERTS_SUCCESS = 'naf/myVehicles/GET_VEHICLE_ALERTS_SUCCESS',
  GET_VEHICLE_ALERTS_FAIL = 'naf/myVehicles/GET_VEHICLE_ALERTS_FAIL',
  GET_VEHICLE_ALERTS_CANCEL = 'naf/myVehicles/GET_VEHICLE_ALERTS_CANCEL',
  REMOVE_VEHICLE_FROM_LIST = 'naf/myVehicles/REMOVE_VEHICLE_FROM_LIST',
  ADD_VEHICLE_TO_LIST = 'naf/myVehicles/ADD_VEHICLE_TO_LIST',
  MERGE_VEHICLE_ALERTS_DATA = 'naf/myVehicles/MERGE_VEHICLE_ALERTS_DATA',
  UPDATE_VEHICLE_MILEAGE = 'naf/myVehicles/UPDATE_VEHICLE_MILEAGE',
}

export interface State {
  vehicleList: {
    data?: Record<VehicleObject['licensePlateNumber'], VehicleObject>;
    isUpdating: boolean;
    errorState?: any;
  };
  vehicle: {
    data?: VehicleObject;
    isUpdating: boolean;
    errorState?: any;
  };
  alerts: {
    data?: VehicleAlertsListResponse;
    isFetching: boolean;
    fetchError?: any;
  };
}

export const initialState: State = {
  vehicleList: {
    data: undefined,
    isUpdating: false,
  },
  vehicle: {
    data: undefined,
    isUpdating: false,
  },
  alerts: {
    data: undefined,
    isFetching: false,
  },
};

export const actions = {
  getMyVehicles: createAsyncAction(
    Actions.GET_MY_VEHICLES, // request payload creator
    Actions.GET_MY_VEHICLES_SUCCESS, // success payload creator
    Actions.GET_MY_VEHICLES_FAIL, // failure payload creator
    Actions.GET_MY_VEHICLES_CANCEL, // optional cancel payload creator
  )<string, MyVehiclesResponse, ReduxError, undefined>(),
  getVehicle: createAsyncAction(
    Actions.GET_VEHICLE,
    Actions.GET_VEHICLE_SUCCESS,
    Actions.GET_VEHICLE_FAIL,
    Actions.GET_VEHICLE_CANCEL,
  )<string, VehicleObject, ReduxError, undefined>(),
  getVehicleAlerts: createAsyncAction(
    Actions.GET_VEHICLE_ALERTS,
    Actions.GET_VEHICLE_ALERTS_SUCCESS,
    Actions.GET_VEHICLE_ALERTS_FAIL,
    Actions.GET_VEHICLE_ALERTS_CANCEL,
  )<string, VehicleAlertsListResponse, ReduxError, undefined>(),
  mergeVehicleAlertsData: createAction(Actions.MERGE_VEHICLE_ALERTS_DATA)<VehicleAlertsResponse>(),
  removeVehicleFromList: createAction(Actions.REMOVE_VEHICLE_FROM_LIST, (licensePlateNumber: string) => ({
    licensePlateNumber,
  }))(),
  addVehicleToList: createAction(Actions.ADD_VEHICLE_TO_LIST, (vehicleObject: VehicleObject) => ({
    vehicleObject,
  }))(),
  updateVehicleMileage: createAction(
    Actions.UPDATE_VEHICLE_MILEAGE,
    (licensePlateNumber: string, mileage: VehicleMileage) => ({ licensePlateNumber, mileage }),
  )(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getMyVehicles.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.vehicleList.isUpdating = true;
    }),
  )
  .handleAction(actions.getMyVehicles.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.vehicleList.isUpdating = false;
      draftState.vehicleList.errorState = false;
      draftState.vehicleList.data = action.payload.vehicles.reduce(
        (acc, cur) => {
          acc[cur.licensePlateNumber] = cur;
          return acc;
        },
        { ...draftState.vehicleList.data },
      );
    }),
  )
  .handleAction(actions.getMyVehicles.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.vehicleList.isUpdating = false;
      draftState.vehicleList.errorState = action.payload;
    }),
  )
  .handleAction(actions.getMyVehicles.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.vehicleList.isUpdating = false;
    }),
  )
  .handleAction(actions.getVehicle.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.vehicle.isUpdating = true;
    }),
  )
  .handleAction(actions.getVehicle.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.vehicle.isUpdating = false;
      draftState.vehicle.errorState = false;
      draftState.vehicle.data = action.payload;
    }),
  )
  .handleAction(actions.getVehicle.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.vehicle.isUpdating = false;
      draftState.vehicle.errorState = action.payload;
    }),
  )
  .handleAction(actions.getVehicle.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.vehicle.isUpdating = false;
    }),
  )
  .handleAction(actions.getVehicleAlerts.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.alerts.isFetching = true;
    }),
  )
  .handleAction(actions.getVehicleAlerts.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.alerts.isFetching = false;
      draftState.alerts.fetchError = undefined;
      draftState.alerts.data = action.payload;
    }),
  )
  .handleAction(actions.getVehicleAlerts.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.alerts.isFetching = false;
      draftState.alerts.fetchError = action.payload;
    }),
  )
  .handleAction(actions.getVehicleAlerts.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.alerts.isFetching = false;
    }),
  )
  .handleAction(actions.mergeVehicleAlertsData, (state = initialState, action) =>
    produce(state, (draftState) => {
      const updatedResult = action.payload.result;
      const existingResults = draftState.alerts.data?.result ?? [];
      const index = existingResults.findIndex(
        (r) => r.vehicleId?.toUpperCase() === updatedResult.vehicleId?.toUpperCase(),
      );
      const newResults =
        index >= 0
          ? [...existingResults.slice(0, index), updatedResult, ...existingResults.slice(index + 1)]
          : [...existingResults, updatedResult];
      draftState.alerts.data = {
        ...action.payload,
        result: newResults,
      };
    }),
  )
  .handleAction(actions.removeVehicleFromList, (state, action) => {
    const newData = { ...state.vehicleList.data };
    delete newData[action.payload.licensePlateNumber];

    return {
      ...state,
      vehicleList: {
        ...state.vehicleList,
        data: newData,
      },
    };
  })
  .handleAction(actions.addVehicleToList, (state, action) => {
    const newData = {
      ...state.vehicleList.data,
      [action.payload.vehicleObject.licensePlateNumber]: action.payload.vehicleObject,
    };

    return {
      ...state,
      vehicleList: {
        ...state.vehicleList,
        data: newData,
      },
    };
  })
  .handleAction(actions.updateVehicleMileage, (state = initialState, action) =>
    produce(state, (draftState) => {
      const vehicle = draftState.vehicleList.data?.[action.payload.licensePlateNumber];
      if (vehicle) {
        vehicle.mileage = action.payload.mileage;
      }
    }),
  );
